/**
 * Editor de texto com formatação, para a janela de escrever.
 *
 * O SERVIDOR JÁ FAZIA A PARTE DELE. `corpo_html` está no contrato desde o
 * começo e o `enviar.go` monta `multipart/alternative` com a parte
 * `text/html` de verdade. O que faltava era só isto — a tela mandava apenas
 * `corpo_texto`. É a mesma forma do anexo: a API aceitava, faltava o botão.
 *
 * SEM BIBLIOTECA, com `contentEditable` e `document.execCommand`.
 *
 * O `execCommand` é oficialmente depreciado, e essa é a objeção honesta. O
 * que pesa do outro lado: ele está implementado em todo navegador que
 * importa, não vai sumir sem aviso de anos, e a alternativa é somar um
 * editor inteiro (TipTap, Quill, Lexical) a um produto que é REDISTRIBUÍDO
 * para o servidor do cliente. É o mesmo critério que manteve os ícones
 * embutidos e que fez o TOTP ser implementado aqui em vez de virar
 * dependência: o que dá para provar contra a especificação não justifica
 * dependência nova.
 *
 * Se um dia o `execCommand` sair de verdade, o que muda é este arquivo —
 * o resto da tela e o servidor inteiro não sabem que ele existe.
 */
import { useCallback, useEffect, useRef } from "react";

import { Icone } from "../ui/Icone";
import type { NomeDeIcone } from "../ui/Icone";

/** Os comandos da barra. `titulo` é o que o leitor de tela anuncia. */
const BOTOES: { cmd: string; icone: NomeDeIcone; titulo: string }[] = [
  { cmd: "bold", icone: "Bold", titulo: "Negrito" },
  { cmd: "italic", icone: "Italic", titulo: "Itálico" },
  { cmd: "underline", icone: "Underline", titulo: "Sublinhado" },
  { cmd: "insertUnorderedList", icone: "List", titulo: "Lista com marcadores" },
  { cmd: "insertOrderedList", icone: "ListOrdered", titulo: "Lista numerada" },
];

export interface EditorProps {
  html: string;
  aoMudar: (html: string) => void;
  id?: string;
  rotulo?: string;
}

export function Editor({ html, aoMudar, id = "corpo", rotulo = "Mensagem" }: EditorProps) {
  const area = useRef<HTMLDivElement>(null);

  // O CONTEÚDO SÓ É ESCRITO DE FORA QUANDO DIVERGE DE VERDADE.
  //
  // Reescrever o innerHTML a cada render destruiria a posição do cursor a
  // cada tecla — o texto "pula" para o fim e a pessoa não consegue corrigir
  // o meio de uma frase. É o defeito clássico de contentEditable controlado.
  useEffect(() => {
    const el = area.current;
    if (!el) return;
    if (el.innerHTML !== html) el.innerHTML = html;
  }, [html]);

  const comando = useCallback(
    (cmd: string) => {
      // O foco precisa estar na área ANTES do comando: `execCommand` age
      // sobre a seleção do documento, e com o foco no botão não há seleção
      // dentro do texto — o clique não faria nada, em silêncio.
      area.current?.focus();
      document.execCommand(cmd);
      if (area.current) aoMudar(area.current.innerHTML);
    },
    [aoMudar],
  );

  const inserirLink = useCallback(() => {
    area.current?.focus();
    const url = window.prompt("Endereço do link (https://…)");
    if (!url) return;
    // SÓ http(s) E mailto. `javascript:` num link que nós mesmos inserimos
    // viraria execução de script na conta de quem RECEBE, assinada com o
    // nosso DKIM. A leitura já higieniza o que chega; isto é a mesma guarda
    // na saída, onde ela custa uma linha.
    if (!/^(https?:|mailto:)/i.test(url.trim())) {
      window.alert("Só aceito endereços http://, https:// ou mailto:.");
      return;
    }
    document.execCommand("createLink", false, url.trim());
    if (area.current) aoMudar(area.current.innerHTML);
  }, [aoMudar]);

  // COLAR ENTRA COMO TEXTO, não como HTML.
  //
  // Colar de uma página traz estilo, script, tabela de layout e fonte de
  // 9px — e o que sai daqui vai assinado em DKIM na caixa de outra pessoa.
  // Entre "preserva a formatação de onde veio" e "não manda lixo hostil com
  // o nosso nome", a segunda ganha. Quem quiser negrito usa a barra.
  const aoColar = useCallback(
    (e: React.ClipboardEvent<HTMLDivElement>) => {
      e.preventDefault();
      const texto = e.clipboardData.getData("text/plain");
      document.execCommand("insertText", false, texto);
      if (area.current) aoMudar(area.current.innerHTML);
    },
    [aoMudar],
  );

  const classeBotao =
    "flex h-7 w-7 items-center justify-center rounded text-tinta-media " +
    "hover:bg-acao-clara hover:text-acao-escura";

  return (
    // SEM CAIXA DENTRO DA CAIXA. As linhas de "Para" e "Assunto" são faixas
    // de largura inteira com borda embaixo; a primeira versão deste editor
    // era um retângulo arredondado flutuando no meio da janela, e destoava
    // de tudo em volta.
    <div className="flex flex-1 flex-col overflow-hidden">
      <div
        className="flex flex-wrap items-center gap-1 border-b border-linha px-3 py-1.5"
        role="toolbar"
        aria-label="Formatação"
      >
        {BOTOES.map((b) => (
          <button
            key={b.cmd}
            type="button"
            title={b.titulo}
            aria-label={b.titulo}
            onClick={() => comando(b.cmd)}
            className={classeBotao}
          >
            <Icone nome={b.icone} tamanho={16} />
          </button>
        ))}
        <span className="mx-1 h-4 w-px bg-linha" aria-hidden="true" />
        <button
          type="button"
          title="Inserir link"
          aria-label="Inserir link"
          onClick={inserirLink}
          className={classeBotao}
        >
          <Icone nome="Link2" tamanho={16} />
        </button>
        <button
          type="button"
          title="Limpar formatação"
          aria-label="Limpar formatação"
          onClick={() => comando("removeFormat")}
          className={classeBotao}
        >
          <Icone nome="RemoveFormatting" tamanho={16} />
        </button>
      </div>

      <label htmlFor={id} className="sr-only">
        {rotulo}
      </label>
      <div
        id={id}
        ref={area}
        contentEditable
        suppressContentEditableWarning
        role="textbox"
        aria-multiline="true"
        aria-label={rotulo}
        onInput={(e) => aoMudar((e.target as HTMLDivElement).innerHTML)}
        onPaste={aoColar}
        className="flex-1 overflow-auto px-4 py-3 text-sm text-tinta outline-none"
      />
    </div>
  );
}

export function htmlParaTexto(html: string): string {
  if (!html) return "";
  const doc = new DOMParser().parseFromString(html, "text/html");
  doc.querySelectorAll("br").forEach((br) => br.replaceWith("\n"));
  // DOIS NÍVEIS DE QUEBRA, e a distinção importa.
  //
  // O `contentEditable` gera uma `<div>` POR LINHA enquanto a pessoa digita.
  // Se `div` valesse linha em branco, todo texto normal sairia com o dobro
  // do espaçamento na versão simples. Parágrafo e citação, esses sim,
  // separam blocos.
  doc.querySelectorAll("p, blockquote, h1, h2, h3, h4, h5, h6").forEach((el) => el.append("\n\n"));
  doc.querySelectorAll("div, li, tr").forEach((el) => el.append("\n"));
  doc.querySelectorAll("li").forEach((el) => el.prepend("- "));
  return (doc.body.textContent ?? "").replace(/\n{3,}/g, "\n\n").trim();
}

/** Escapa texto simples para virar HTML — usado no rodapé e na assinatura. */
export function textoParaHTML(texto: string): string {
  const div = document.createElement("div");
  div.textContent = texto;
  return div.innerHTML.replace(/\n/g, "<br>");
}
